
import { supabase } from '@/integrations/supabase/client';
import { LeadStatus, LeadStatusHistory } from '@/types/lead';

// Update a lead's status
export const updateLeadStatus = async (
  leadId: string, 
  newStatus: LeadStatus, 
  notes?: string
): Promise<boolean> => {
  try {
    // Get the current user ID
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    // Begin a transaction by starting with the status update on the leads table
    const { error: updateError } = await supabase
      .from('leads')
      .update({ 
        status: newStatus,
        updated_at: new Date().toISOString()
      })
      .eq('id', leadId);
      
    if (updateError) throw updateError;
    
    // Then record the status change in the history table
    const { error: historyError } = await supabase
      .from('lead_status_history')
      .insert({
        lead_id: leadId,
        status: newStatus,
        notes: notes || null,
        created_by: userId || null
      });
      
    if (historyError) throw historyError;
    
    return true;
  } catch (error) {
    console.error('Error updating lead status:', error);
    return false;
  }
};

// Get status history for a lead
export const getLeadStatusHistory = async (leadId: string): Promise<LeadStatusHistory[]> => {
  try {
    // Modified query to correctly join with profiles table using the foreign key
    const { data, error } = await supabase
      .from('lead_status_history')
      .select(`
        id,
        lead_id,
        status,
        notes,
        created_at,
        created_by,
        profiles:created_by (
          first_name,
          last_name
        )
      `)
      .eq('lead_id', leadId)
      .order('created_at', { ascending: false });
      
    if (error) throw error;
    
    return data.map(item => ({
      id: item.id,
      leadId: item.lead_id,
      status: item.status as LeadStatus,
      notes: item.notes || undefined,
      createdAt: item.created_at,
      createdBy: item.profiles ? 
        `${item.profiles.first_name || ''} ${item.profiles.last_name || ''}`.trim() || undefined : 
        undefined
    }));
  } catch (error) {
    console.error('Error fetching lead status history:', error);
    return [];
  }
};

// Calculate average time in each status
export const calculateTimeInStatus = async (leadId: string): Promise<Record<LeadStatus, number>> => {
  const history = await getLeadStatusHistory(leadId);
  const result: Partial<Record<LeadStatus, number>> = {};
  
  if (history.length <= 1) {
    return {} as Record<LeadStatus, number>;
  }
  
  // Sort by created date ascending to calculate time differences
  const sortedHistory = [...history].sort((a, b) => 
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );
  
  for (let i = 0; i < sortedHistory.length - 1; i++) {
    const status = sortedHistory[i].status;
    const startDate = new Date(sortedHistory[i].createdAt);
    const endDate = new Date(sortedHistory[i + 1].createdAt);
    const durationDays = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
    
    if (result[status]) {
      result[status] = (result[status] || 0) + durationDays;
    } else {
      result[status] = durationDays;
    }
  }
  
  return result as Record<LeadStatus, number>;
};
